<template>
  <div class="reset-container">
    <div class="reset-card">
      
      <div class="image-container">
        <img 
          src="/undraw_enter-password_1kl4.png" 
          alt="Restablecer contraseña" 
          class="reset-image"
        >
      </div>
      
      <h2>Restablecer contraseña</h2>
      <p v-if="!success">Ingresa tu nueva contraseña</p>
      
      <!-- MENSAJE DE ÉXITO CON BOTÓN PARA VOLVER A LA APP -->
      <div v-if="success" class="alert success">
        <i class="material-icons success-icon">check_circle</i>
        <p class="success-title">¡Contraseña actualizada!</p>
        <p class="success-message">{{ successMessage }}</p>
        
        <div class="success-actions">
          <button class="btn-back-to-app" @click="goBackToApp">
            <i class="material-icons">arrow_back</i>
            Volver a la aplicación
          </button>
          <router-link to="/login" class="alert-link">
            Ir al inicio de sesión
          </router-link>
        </div>
      </div>
      
      <div v-if="error" class="alert error">{{ errorMessage }}</div>

      <form v-if="!success" @submit.prevent="onSubmit">
        <div class="form-group">
          <div class="password-wrapper">
            <input 
              :type="showPassword ? 'text' : 'password'" 
              v-model="password" 
              placeholder="Nueva contraseña"
              required
              :class="{ 'is-invalid': error && !password }"
            >
            <button type="button" class="toggle-password" @click="togglePasswordVisibility">
              <i class="material-icons">{{ showPassword ? 'visibility_off' : 'visibility' }}</i>
            </button>
          </div>
        </div>
        
        <div class="form-group">
          <div class="password-wrapper">
            <input 
              :type="showConfirmPassword ? 'text' : 'password'" 
              v-model="passwordConfirmation" 
              placeholder="Confirmar contraseña"
              required
              :class="{ 'is-invalid': error && password !== passwordConfirmation }"
            >
            <button type="button" class="toggle-password" @click="toggleConfirmPasswordVisibility">
              <i class="material-icons">{{ showConfirmPassword ? 'visibility_off' : 'visibility' }}</i>
            </button>
          </div>
        </div>
        
        <button type="submit" :disabled="loading" class="btn-reset">
          <span v-if="loading" class="spinner"></span>
          {{ loading ? 'Restableciendo...' : 'Restablecer contraseña' }}
        </button>
      </form>

      <div v-if="!success" class="text-center">
        <router-link to="/login" class="back-link">Volver al inicio de sesión</router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'  // 🔥 Solo useRoute, no useRouter
import api from '../../services/api'

const route = useRoute()  // Solo route, no router
const password = ref('')
const passwordConfirmation = ref('')
const loading = ref(false)
const success = ref(false)
const error = ref(false)
const successMessage = ref('')
const errorMessage = ref('')
const token = ref('')
const email = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)

// Variable para controlar si la app es PWA o Web
const isPWA = ref(false)

onMounted(() => {
  token.value = route.query.token
  email.value = route.query.email
  
  // Detectar si está instalada como PWA
  isPWA.value = window.matchMedia('(display-mode: standalone)').matches || 
                window.navigator.standalone === true
  
  if (!token.value || !email.value) {
    error.value = true
    errorMessage.value = 'Enlace inválido o expirado'
  }
})

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}

const toggleConfirmPasswordVisibility = () => {
  showConfirmPassword.value = !showConfirmPassword.value
}

const goBackToApp = () => {
  // Intentar cerrar la pestaña actual
  window.close()
  
  // Si la app está instalada como PWA, intentar abrirla
  if (isPWA.value) {
    setTimeout(() => {
      alert('Si la pestaña no se cierra automáticamente, por favor vuelve manualmente a la aplicación.')
    }, 500)
  } else {
    // Redirigir a la URL raíz de la app
    setTimeout(() => {
      window.location.href = '/'
    }, 500)
  }
}

const onSubmit = async () => {
  // Validaciones
  if (!password.value) {
    error.value = true
    errorMessage.value = 'Ingresa una nueva contraseña'
    return
  }
  
  if (password.value.length < 8) {
    error.value = true
    errorMessage.value = 'La contraseña debe tener al menos 8 caracteres'
    return
  }
  
  if (password.value !== passwordConfirmation.value) {
    error.value = true
    errorMessage.value = 'Las contraseñas no coinciden'
    return
  }
  
  loading.value = true
  error.value = false
  errorMessage.value = ''
  
  try {
    await api.post('/reset-password', {
      token: token.value,
      email: email.value,
      password: password.value,
      password_confirmation: passwordConfirmation.value
    })
    
    success.value = true
    successMessage.value = 'Tu contraseña ha sido cambiada exitosamente.'
    
  } catch (err) {
    error.value = true
    errorMessage.value = err.response?.data?.message || 'Error al restablecer la contraseña'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.reset-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 20px;
}

.reset-card {
  background: white;
  padding: 40px 30px;
  width: 100%;
  max-width: 400px;
  text-align: center;
}

.image-container {
  margin-bottom: 25px;
  display: flex;
  justify-content: center;
}

.reset-image {
  width: 180px;
  height: auto;
  object-fit: contain;
}

h2 {
  color: #00144a;
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 10px;
}

p {
  color: #666;
  font-size: 14px;
  margin-bottom: 25px;
}

.alert {
  padding: 20px;
  border-radius: 16px;
  margin-bottom: 20px;
  font-size: 14px;
  text-align: center;
}

.alert.success {
  background: #e8f5e9;
  border: 1px solid #c8e6c9;
}

.alert.error {
  background: #ffebee;
  color: #f12711;
  border-left: 4px solid #f12711;
}

.success-icon {
  font-size: 56px;
  color: #4caf50;
  margin-bottom: 12px;
}

.success-title {
  font-size: 18px;
  font-weight: bold;
  color: #2e7d32;
  margin-bottom: 8px;
}

.success-message {
  color: #2e7d32;
  margin-bottom: 20px;
}

.success-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 10px;
}

.btn-back-to-app {
  background: #00144a;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 30px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  width: 100%;
}

.btn-back-to-app:hover {
  background: #002a73;
  transform: translateY(-2px);
}

.btn-back-to-app:active {
  transform: translateY(0);
}

.alert-link {
  display: inline-block;
  color: #2e7d32;
  text-decoration: none;
  font-weight: 600;
  font-size: 13px;
}

.alert-link:hover {
  text-decoration: underline;
}

.form-group {
  margin-bottom: 20px;
}

.password-wrapper {
  position: relative;
}

input {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #ddd;
  border-radius: 12px;
  font-size: 16px;
  transition: all 0.3s;
  background: #f9f9f9;
}

input:focus {
  outline: none;
  border-color: #00144a;
  background: white;
  box-shadow: 0 0 0 3px rgba(0, 20, 74, 0.1);
}

input.is-invalid {
  border-color: #f12711;
  background: rgba(241, 39, 17, 0.05);
}

.toggle-password {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  padding: 0;
}

.toggle-password i {
  font-size: 20px;
}

.toggle-password:hover {
  color: #00144a;
}

.btn-reset {
  width: 100%;
  padding: 14px;
  background: #00144a;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
}

.btn-reset:hover {
  background: #002a73;
  transform: translateY(-2px);
}

.btn-reset:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.text-center {
  text-align: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.back-link {
  color: #a50000;
  text-decoration: none;
  font-size: 14px;
}

.back-link:hover {
  text-decoration: underline;
}

@media (max-width: 480px) {
  .reset-card {
    padding: 30px 20px;
  }
  
  .reset-image {
    width: 150px;
  }
  
  h2 {
    font-size: 1.3rem;
  }
  
  input {
    padding: 12px 14px;
    font-size: 14px;
  }
  
  .btn-reset {
    padding: 12px;
  }
  
  .success-icon {
    font-size: 48px;
  }
  
  .success-title {
    font-size: 16px;
  }
}
</style>